import React, { useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { withPageProtection } from '@/lib/rbac/hoc'
import { PAGES } from '@/lib/rbac/constants'
import { MetricCard } from '@/components/MetricCard'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { HTMLSelect as Select } from '@/components/ui/Select'
import { Badge } from '@/components/ui/Badge'
import { Card } from '@/components/ui/Card'
import { EmptyState } from '@/components/ui/EmptyState'
import { ReassignManagerModal } from '@/components/workforce/ReassignManagerModal'
import { EmployeeAutocomplete } from '@/components/workforce/EmployeeAutocomplete'
import { HighlightedText } from '@/components/common/HighlightedText'
import { SkeletonCard } from '@/components/common/SkeletonCard'
import { useEmployees, useEmployeeStats, useDepartments, useRoles } from '@/hooks/useWorkforce'
import { useReassignManager } from '@/hooks/useWorkforceMutations'
import { useWorkforceUIStore } from '@/stores/workforce-ui-store'
import { Employee } from '@/types/workforce'
import { Link } from '@tanstack/react-router'
import { usePageTitle } from '@/stores/page-title-store'
import {
  Users,
  UserPlus,
  Search,
  Filter,
  Download,
  MoreHorizontal,
  Mail,
  Phone,
  Calendar,
  Building2,
  Briefcase,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  UserCog,
  Edit,
  X,
  Loader2,
  RefreshCw
} from 'lucide-react'

function EmployeesContent() {
  const { user } = useAuth()
  usePageTitle('Colaboradores')

  // 🚀 Server State (TanStack Query)
  const {
    employeesFilters,
    setEmployeesFilters,
    resetEmployeesFilters,
    employeesSort,
    setEmployeesSort
  } = useWorkforceUIStore()

  // 📄 Pagination state
  const [accumulatedEmployees, setAccumulatedEmployees] = useState<Employee[]>([])
  const [currentNextToken, setCurrentNextToken] = useState<string | undefined>(undefined)
  const [isFirstPage, setIsFirstPage] = useState(true)

  // Build pagination params
  const paginationParams = React.useMemo(() => ({
    limit: 20,
    nextToken: currentNextToken,
    sortBy: employeesSort.sortBy,
    sortOrder: employeesSort.sortOrder
  }), [currentNextToken, employeesSort.sortBy, employeesSort.sortOrder])

  const {
    employees,
    loading,
    isFetching,
    error,
    nextToken,
    refetch
  } = useEmployees(
    {
      status: employeesFilters.status === 'all' ? undefined : employeesFilters.status,
      departmentId: employeesFilters.departmentId,
      roleId: employeesFilters.roleId
    },
    paginationParams
  )

  const { stats, loading: statsLoading } = useEmployeeStats()
  const { departments, loading: departmentsLoading } = useDepartments()
  const { roles, loading: rolesLoading } = useRoles()

  // 🔄 Mutations
  const reassignManagerMutation = useReassignManager()

  // 🎯 Local State (ephemeral UI state only)
  const [showFilters, setShowFilters] = useState(false)
  const [employeeToReassign, setEmployeeToReassign] = useState<Employee | null>(null)
  const [currentManagerForModal, setCurrentManagerForModal] = useState<Employee | undefined>(undefined)

  // Reset pagination when filters change
  React.useEffect(() => {
    console.log('>>> FILTERS CHANGED - Resetting pagination')
    setAccumulatedEmployees([])
    setCurrentNextToken(undefined)
    setIsFirstPage(true)
  }, [employeesFilters.status, employeesFilters.departmentId, employeesFilters.roleId])

  // Accumulate employees when new page loads
  React.useEffect(() => {
    if (!loading && employees.length > 0) {
      setAccumulatedEmployees(prev => {
        // First page - replace all
        if (isFirstPage) {
          console.log('>>> FIRST PAGE - Setting', employees.length, 'employees')
          return employees
        }
        // Subsequent pages - merge, avoiding duplicates
        const newEmployees = employees.filter(
          emp => !prev.some(existing => existing.employeeId === emp.employeeId)
        )
        console.log('>>> NEXT PAGE - Adding', newEmployees.length, 'new to', prev.length, 'existing')
        return [...prev, ...newEmployees]
      })
    }
  }, [employees, loading, isFirstPage])

  // Use accumulated employees as the source of truth
  const allEmployees = accumulatedEmployees

  // Enrich employees with department and role names
  const enrichedEmployees = React.useMemo(() => {
    return allEmployees.map((employee): Employee => {
      // Map employment to currentEngagement if it doesn't exist
      if (!employee.currentEngagement && employee.employment) {
        const deptId = employee.employment.departmentId?.toString()
        const roleId = employee.employment.roleId?.toString()

        const department = departments.find(d => d.departmentId === deptId)
        const role = roles.find(r => r.roleId === roleId)

        return {
          ...employee,
          currentEngagement: {
            engagementId: employee.integrationData?.personId,
            contractType: employee.employment.contractType,
            status: employee.employment.status as any,
            startDate: employee.employment.startDate,
            endDate: employee.employment.endDate ?? null,
            departmentId: deptId || null,
            departmentName: department?.name || null,
            roleId: roleId || null,
            roleName: role?.name || null,
            managerId: employee.managerId,
            salary: employee.employment.salary || null
          }
        }
      }
      return employee
    })
  }, [allEmployees, departments, roles])

  // No client-side filtering - autocomplete navigates directly to employee detail
  const filteredEmployees = enrichedEmployees

  // Debug: log final state
  console.log('>>> RENDER STATE:', {
    employeesFromAPI: employees.length,
    allEmployees: allEmployees.length,
    enrichedEmployees: enrichedEmployees.length,
    filteredEmployees: filteredEmployees.length,
    nextToken: nextToken ? 'exists' : 'null',
    loading,
    isFetching
  })

  // Derived state
  const isInitialLoading = (loading || statsLoading || departmentsLoading || rolesLoading) && allEmployees.length === 0
  const isLoadingMore = isFetching && allEmployees.length > 0
  const hasNextPage = !!nextToken

  // Handler for loading more employees
  const handleLoadMore = () => {
    console.log('>>> LOAD MORE CLICKED')
    console.log('>>> Current nextToken from API:', nextToken)
    if (nextToken && !isFetching) {
      console.log('>>> Fetching next page')
      setCurrentNextToken(nextToken)
      setIsFirstPage(false)
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
        return <CheckCircle className="w-4 h-4 text-success" />
      case 'INACTIVE':
        return <XCircle className="w-4 h-4 text-brand-gray" />
      case 'PENDING':
        return <Clock className="w-4 h-4 text-warning" />
      case 'TERMINATED':
        return <AlertTriangle className="w-4 h-4 text-error" />
      default:
        return <Clock className="w-4 h-4 text-brand-gray" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
        return 'bg-success/20 text-success'
      case 'INACTIVE':
        return 'bg-brand-gray/20 text-brand-gray'
      case 'PENDING':
        return 'bg-warning/20 text-warning'
      case 'TERMINATED':
        return 'bg-error/20 text-error'
      default:
        return 'bg-brand-gray/20 text-brand-gray'
    }
  }

  const formatDate = (date: Date | string | null | undefined) => {
    if (!date) return 'N/A'
    try {
      const dateObj = new Date(date)
      if (isNaN(dateObj.getTime())) return 'N/A'
      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }).format(dateObj)
    } catch {
      return 'N/A'
    }
  }

  const formatCurrency = (amount?: number) => {
    if (!amount) return 'N/A'
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(amount)
  }

  // Handlers
  const handleReassignManager = async (employee: Employee) => {
    setEmployeeToReassign(employee)
    // Manager relationship not currently stored in engagement model
    setCurrentManagerForModal(undefined)
  }

  const handleReassignConfirm = async (newManagerId: string | null) => {
    if (!employeeToReassign) return

    try {
      // ✅ Use mutation hook - automatically handles cache invalidation and toasts
      await reassignManagerMutation.mutateAsync({
        employeeId: employeeToReassign.employeeId,
        newManagerId
      })

      // Close modal
      setEmployeeToReassign(null)
      setCurrentManagerForModal(undefined)
    } catch (error) {
      // Error toast is handled by mutation hook
      console.error('Error reassigning manager:', error)
      throw error // Re-throw to let modal handle the error display
    }
  }

  const handleReassignCancel = () => {
    setEmployeeToReassign(null)
    setCurrentManagerForModal(undefined)
  }

  return (
    <>
    <div className="space-y-6">
        {/* Action Buttons */}
        <div className="flex justify-end">
          <Button variant="ghost" size="sm">
            <Download className="w-4 h-4 mr-2" />
            Exportar
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="dashboard-grid lg:grid-cols-4">
            <MetricCard
              title="Total de Colaboradores"
              value={stats.totalEmployees}
              icon={Users}
              trend="neutral"
            />
            <MetricCard
              title="Colaboradores Ativos"
              value={stats.activeEmployees}
              change={`${Math.round((stats.activeEmployees / stats.totalEmployees) * 100)}%`}
              icon={CheckCircle}
              trend="up"
            />
            <MetricCard
              title="Demitidos"
              value={stats.terminatedEmployees}
              icon={AlertTriangle}
              trend="neutral"
            />
            <MetricCard
              title="Inativos"
              value={stats.inactiveEmployees}
              icon={XCircle}
              trend="neutral"
            />
          </div>
        )}

        {/* Search and Filters */}
        <Card className="p-6 overflow-visible">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="flex-1 relative" style={{ zIndex: 1 }}>
              {/* Server-side autocomplete search */}
              <EmployeeAutocomplete
                placeholder="Buscar funcionário por nome ou email..."
                navigateOnSelect={true}
              />
            </div>
            <div className="flex gap-3">
              <select
                value={employeesFilters.departmentId?.toString() || ''}
                onChange={(e) => {
                  const value = e.target.value
                  console.log('>>> Department select changed:', value)
                  setEmployeesFilters({ departmentId: value || undefined })
                }}
                className="form-select px-3 py-2 border border-brand-gray/30 rounded-monumenta bg-brand-dark-lighter text-white"
              >
                <option value="">Todos os departamentos</option>
                {departments.map(dept => (
                  <option key={dept.departmentId} value={dept.departmentId}>{dept.name}</option>
                ))}
              </select>
              <select
                value={employeesFilters.roleId?.toString() || ''}
                onChange={(e) => {
                  const value = e.target.value
                  setEmployeesFilters({ roleId: value || undefined })
                }}
                className="form-select px-3 py-2 border border-brand-gray/30 rounded-monumenta bg-brand-dark-lighter text-white"
              >
                <option value="">Todos os cargos</option>
                {roles.map(role => (
                  <option key={role.roleId} value={role.roleId}>{role.name}</option>
                ))}
              </select>
              <Select
                value={employeesFilters.status || 'all'}
                onValueChange={(value) => setEmployeesFilters({ status: value as any })}
              >
                <option value="all">Todos os status</option>
                <option value="ACTIVE">Ativo</option>
                <option value="INACTIVE">Inativo</option>
                <option value="TERMINATED">Demitido</option>
              </Select>
              {(employeesFilters.departmentId || employeesFilters.roleId || employeesFilters.status !== 'all' || employeesFilters.search) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    resetEmployeesFilters()
                  }}
                  title="Limpar filtros"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={() => setShowFilters(!showFilters)}>
                <Filter className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </Card>

        {/* Employee List */}
        {isInitialLoading ? (
          <div className="space-y-3">
            <SkeletonCard count={8} />
          </div>
        ) : filteredEmployees.length === 0 ? (
          <EmptyState
            icon={Users}
            title="Nenhum colaborador encontrado"
            description="Não há colaboradores que correspondam aos critérios de busca."
          />
        ) : (
          <>
            <div className="grid gap-4">
              {filteredEmployees.map((employee) => {
                const name = employee.personalInfo?.name || 'Nome não informado'
                const nameParts = name.split(' ').filter(p => p.length > 0)
                const initials = nameParts.length >= 2 ?
                  `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}` :
                  nameParts.length === 1 ?
                  `${nameParts[0][0]}${nameParts[0][1] || 'N'}` :
                  'NA'

                return (
                  <Card key={employee.employeeId} className="card-hover p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4 flex-1">
                        <div className="w-12 h-12 bg-gradient-to-br from-brand-primary to-brand-accent-600 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {employee.personalInfo?.pictureUrl ? (
                            <img
                              src={employee.personalInfo.pictureUrl}
                              alt={name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-sm font-bold text-brand-dark">
                              {initials.toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-2">
                            <Link to={`/workforce/employees/${employee.employeeId}` as any}>
                              <h3 className="font-display text-lg font-semibold text-white hover:text-brand-primary transition-colors">
                                {name}
                              </h3>
                            </Link>
                            <Badge className={getStatusColor(employee.status)}>
                              <div className="flex items-center gap-1">
                                {getStatusIcon(employee.status)}
                                <span className="capitalize">{employee.status?.toLowerCase()}</span>
                              </div>
                            </Badge>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm text-brand-gray">
                            <div className="flex items-center gap-2">
                              <Mail className="w-4 h-4" />
                              <span className="truncate">{employee.email || 'N/A'}</span>
                            </div>
                            {employee.personalInfo?.phone && (
                              <div className="flex items-center gap-2">
                                <Phone className="w-4 h-4" />
                                <span>{employee.personalInfo.phone}</span>
                              </div>
                            )}
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4" />
                              <span>Dept. {employee.currentEngagement?.departmentName || 'N/A'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Briefcase className="w-4 h-4" />
                              <span>Cargo {employee.currentEngagement?.roleName || 'N/A'}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 mt-3 text-sm text-brand-gray">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4" />
                              <span>Admissão: {formatDate(employee.currentEngagement?.startDate)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleReassignManager(employee)}
                          className="border-brand-primary/30 hover:bg-brand-primary/10 hover:border-brand-primary text-brand-primary"
                        >
                          <UserCog className="w-4 h-4 mr-1.5" />
                          <span className="hidden sm:inline">Gestor</span>
                        </Button>
                        <Link to={`/workforce/employees/${employee.employeeId}/edit` as any}>
                          <Button variant="ghost" size="sm">
                            <Edit className="w-4 h-4 sm:mr-1.5" />
                            <span className="hidden sm:inline">Editar</span>
                          </Button>
                        </Link>
                        <Button variant="ghost" size="sm">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                )
              })}
            </div>

            {/* Loading More Indicator */}
            {isLoadingMore && (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-brand-gray">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Carregando mais colaboradores...</span>
              </div>
            )}

            {/* Load More Button */}
            {hasNextPage && !isLoadingMore && (
              <div className="flex justify-center mt-6">
                <Button
                  variant="ghost"
                  onClick={handleLoadMore}
                >
                  Carregar Mais
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Reassign Manager Modal */}
      {employeeToReassign && (
        <ReassignManagerModal
          employee={employeeToReassign}
          currentManager={currentManagerForModal}
          onConfirm={handleReassignConfirm}
          onCancel={handleReassignCancel}
        />
      )}
    </>
  )
}

// Apply RBAC protection - only admin can access workforce employees
export default withPageProtection(EmployeesContent, PAGES.WORKFORCE_EMPLOYEES)